import React, { useEffect, useState } from "react";
import api, { inr } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import { Plus, X, Trash2, Shirt } from "lucide-react";
import { toast } from "sonner";
import PickerWithAdd, { createCustomer, createProduct } from "@/components/PickerWithAdd";
import TShirtDiagram, { totalPrintCost } from "@/components/TShirtDiagram";
import DetailPopup from "@/components/DetailPopup";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { usePagination } from "@/components/Paginator";
import MobileLineWizard from "@/components/MobileLineWizard";
import FinalReview from "@/components/FinalReview";

const stageBadge = {
  procurement: "badge-warn",
  material_received: "badge-info",
  customization: "badge-warn",
  ready: "badge-info",
  dispatched: "badge-success",
  delivered: "badge-success",
};
const SIZES = ["XS","S","M","L","XL","2XL","3XL","4XL"];
const COMMON_COLORS = ["Black","White","Navy","Grey","Red","Blue","Green","Maroon","Beige","Yellow"];
const CUSTOMIZATION_TYPES = ["","Embroidery","DTF Print","Screen Print","UV Print","Laser Engraving","Plain (no customization)"];
const emptyItem = () => ({ product: "", color: "", qty: 1, unit_price: 0, customization: "", customization_price: 0, other_customization: "", other_customization_cost: 0, other_size: "", gst_percent: 5, oversize_surcharge: 0, hsn_code: "", category: "", sizes: {}, print_locations: {} });

function OrdersTable({ items, onRowClick, onDeleted }) {
  const { pageItems, controls } = usePagination(items, 20);
  return (
    <>
      <table className="data-table">
        <thead><tr>
          <th>Order #</th><th>Customer</th><th>Items</th><th>Total</th><th>Stage</th><th>Actions</th>
        </tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-[#757169]">No orders yet — create one above or accept a quote.</td></tr>}
          {pageItems.map(o => (
            <tr key={o.id} data-testid={`order-row-${o.id}`} className="cursor-pointer" onClick={() => onRowClick(o)}>
              <td className="mono">{o.order_no}</td>
              <td>{o.customer_name}</td>
              <td>{o.items?.length || 0}</td>
              <td className="mono">{inr(o.total)}</td>
              <td><span className={`badge ${stageBadge[o.stage] || "badge-neutral"}`}>{o.stage}</span></td>
              <td onClick={e => e.stopPropagation()}>
                <div className="flex items-center gap-1">
                  <Link className="btn-ghost text-xs" to={`/orders/${o.id}`} data-testid={`order-open-${o.id}`}>Open →</Link>
                  <AdminDeleteButton endpoint={`/orders/${o.id}`} label={`order ${o.order_no}`} onDone={onDeleted} testid={`delete-order-${o.id}`} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {controls && <div className="px-2 pb-3">{controls}</div>}
    </>
  );
}

export default function Orders() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [popup, setPopup] = useState(null);
  const [openTee, setOpenTee] = useState(-1);
  const [form, setForm] = useState({
    customer_id: "", customer_name: "", customer_phone: "",
    advance_amount: 0, expected_delivery_at: "", notes: "",
    manual_order_id: "",
    physical_form_url: "", physical_form_name: "",
    items: [emptyItem()],
  });
  const nav = useNavigate();

  const load = async () => {
    setItems((await api.get("/orders")).data);
    setCustomers((await api.get("/customers")).data);
    setProducts((await api.get("/products")).data);
  };
  useEffect(() => { load(); }, []);

  const customerOpts = customers.map(c => ({ value: c.id, label: c.name, meta: c }));
  const productOpts = products.map(p => ({ value: p.name, label: p.name, meta: p }));

  const addRow = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const rmRow = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setRow = (i, k, v) => setForm(f => ({
    ...f, items: f.items.map((it, idx) => idx === i ? { ...it, [k]: v } : it)
  }));

  const applyProductPrefill = (i, prod) => {
    if (!prod) return;
    setForm(f => ({...f, items: f.items.map((it, idx) => idx === i ? {
      ...it, product: prod.name,
      unit_price: prod.default_price || it.unit_price,
      gst_percent: (prod.default_gst != null ? Number(prod.default_gst) : it.gst_percent),
      hsn_code: prod.hsn_code || it.hsn_code,
      category: prod.category || it.category,
      oversize_surcharge: (prod.oversize_surcharge != null ? Number(prod.oversize_surcharge) : it.oversize_surcharge || 0),
    } : it)}));
  };

  const OVERSIZE_KEYS = ["3XL","4XL","5XL"];
  const oversizeCount = (item) => {
    const s = item.sizes || {};
    return OVERSIZE_KEYS.reduce((a, k) => a + Number(s[k] || 0), 0);
  };

  const handlePhysicalUpload = (file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm(f => ({ ...f, physical_form_url: reader.result, physical_form_name: file.name }));
    reader.readAsDataURL(file);
  };

  const totals = (() => {
    const slabs = {};
    let sub = 0;
    form.items.forEach(b => {
      const cust = Number(b.customization_price)||0;
      const other = Number(b.other_customization_cost)||0;
      const over = Number(b.oversize_surcharge)||0;
      const oversizeExtra = over * oversizeCount(b);
      const lineSub = (Number(b.qty)||0)*((Number(b.unit_price)||0) + cust + other) + oversizeExtra;
      const slab = Number(b.gst_percent ?? 5);
      const lineGst = lineSub * slab / 100;
      sub += lineSub;
      if (!slabs[slab]) slabs[slab] = { subtotal: 0, gst: 0, total: 0 };
      slabs[slab].subtotal += lineSub;
      slabs[slab].gst += lineGst;
      slabs[slab].total += lineSub + lineGst;
    });
    const gst = Object.values(slabs).reduce((a,s)=>a+s.gst,0);
    const breakup = Object.entries(slabs).sort(([a],[b])=>Number(a)-Number(b))
      .map(([slab,v]) => ({ slab: Number(slab), ...v }));
    return { sub, gst, total: sub+gst, breakup };
  })();

  const [showReview, setShowReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submitPreview = (e) => {
    e.preventDefault();
    if (!form.customer_name) return toast.error("Pick or add a customer");
    if (!form.items.some(i => i.product)) return toast.error("Add at least one line item with a product");
    setShowReview(true);
  };
  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/orders", form);
      toast.success(`Order ${data.order_no} created`);
      setShowReview(false);
      setShowForm(false);
      setForm({
        customer_id: "", customer_name: "", customer_phone: "",
        advance_amount: 0, expected_delivery_at: "", notes: "", manual_order_id: "",
        physical_form_url: "", physical_form_name: "",
        items: [emptyItem()],
      });
      load();
      nav(`/orders/${data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSubmitting(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="label-tiny mb-1">Production</div>
          <h1 className="font-display text-3xl tracking-tight">Orders</h1>
          <p className="text-sm text-[#757169] mt-1">Live production status. Click any row for a quick view or open to advance stages.</p>
        </div>
        <button className="btn-primary flex items-center gap-2" onClick={() => setShowForm(v => !v)} data-testid="orders-new-btn">
          {showForm ? <><X size={16}/> Cancel</> : <><Plus size={16}/> New order</>}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submitPreview} className="card-surface p-6 space-y-5 fade-up" data-testid="orders-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="md:col-span-2">
              <div className="label-tiny mb-1">Customer *</div>
              <PickerWithAdd testid="orders-field-customer"
                requirePhone
                value={form.customer_id}
                onChange={(v, meta) => setForm({ ...form, customer_id: v, customer_name: meta?.name || "", customer_phone: meta?.phone || form.customer_phone })}
                options={customerOpts}
                placeholder="Choose a customer"
                addLabel="customer"
                onCreate={async (name, phone) => { const o = await createCustomer(name, phone); setCustomers(cs => [o.meta, ...cs]); return o; }}
              />
            </div>
            <div><div className="label-tiny mb-1">Phone</div>
              <input className="input-field" value={form.customer_phone} onChange={e => setForm({ ...form, customer_phone: e.target.value })}/></div>
            <div><div className="label-tiny mb-1">Advance ₹</div>
              <input type="number" className="input-field" value={form.advance_amount} onChange={e => setForm({ ...form, advance_amount: Number(e.target.value) })} data-testid="order-advance"/></div>
            <div><div className="label-tiny mb-1">Expected delivery</div>
              <input type="date" className="input-field" value={form.expected_delivery_at} onChange={e => setForm({ ...form, expected_delivery_at: e.target.value })}/></div>
            <div><div className="label-tiny mb-1">Manual order # (optional)</div>
              <input className="input-field" value={form.manual_order_id} onChange={e => setForm({ ...form, manual_order_id: e.target.value })} placeholder="Book #"/></div>
            <div className="md:col-span-2"><div className="label-tiny mb-1">Notes</div>
              <input className="input-field" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Optional"/></div>
          </div>

          {/* Physical order form photo/scan */}
          <div className="p-3 border border-dashed border-[#EAE5DB] rounded-md bg-[#FBF8F1]">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <div className="label-tiny">Photo / scan of physical order form <span className="normal-case text-[10px]">(optional)</span></div>
                <div className="text-[10px] text-[#75706A]">Take a picture of the paper booking so it&apos;s on file with this order.</div>
              </div>
              <label className="btn-ghost text-xs cursor-pointer inline-flex items-center gap-1">
                {form.physical_form_url ? "Replace photo" : "Choose or take photo"}
                <input type="file" accept="image/*" capture="environment" hidden onChange={e => handlePhysicalUpload(e.target.files?.[0])} data-testid="order-physical-form-input"/>
              </label>
            </div>
            {form.physical_form_url && (
              <div className="mt-2 flex items-center gap-3">
                <img src={form.physical_form_url} alt="physical order" className="max-h-24 rounded border border-[#EAE5DB]"/>
                <div className="text-xs text-[#75706A]">{form.physical_form_name}</div>
                <button type="button" className="btn-ghost text-xs text-[#B83A3A]" onClick={()=>setForm(f=>({...f, physical_form_url:"", physical_form_name:""}))}>Remove</button>
              </div>
            )}
          </div>
          <p className="text-[10px] text-[#75706A]">GST is product-wise. Each line item pulls its own GST slab from the product master.</p>

          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="label-tiny">Line items</div>
              <button type="button" onClick={addRow} className="btn-ghost text-xs flex items-center gap-1"><Plus size={12}/> Add line</button>
            </div>
            <div className="space-y-3">
              {form.items.map((it, i) => (
                <React.Fragment key={i}>
                  {/* MOBILE stepped wizard */}
                  <div className="md:hidden">
                    <MobileLineWizard
                      index={i}
                      item={it}
                      productOpts={productOpts}
                      oversizeCount={oversizeCount}
                      removable={form.items.length > 1}
                      onRemove={() => rmRow(i)}
                      onProductPick={(meta) => applyProductPrefill(i, meta)}
                      setField={(k, v) => setRow(i, k, v)}
                      setSize={(sz, raw) => {
                        const v = raw === "" ? 0 : Number(raw) || 0;
                        const sizes = { ...(it.sizes || {}) };
                        if (!v) delete sizes[sz]; else sizes[sz] = v;
                        const totalQty = Object.values(sizes).reduce((a, b) => a + Number(b || 0), 0);
                        setForm(f => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, sizes, qty: totalQty || x.qty } : x) }));
                      }}
                      onColorCreate={{
                        forProduct: async (name) => { const o = await createProduct(name); setProducts(ps => [o.meta, ...ps]); return o; },
                        forColor: async (name) => ({ value: name, label: name }),
                      }}
                    />
                  </div>
                  {/* DESKTOP inline row */}
                  <div className="hidden md:block border border-[#EAE5DB] rounded-md p-3 bg-[#FBF8F1]">
                  <div className="grid grid-cols-2 md:grid-cols-12 gap-2 items-end">
                    <div className="col-span-2 md:col-span-4"><div className="label-tiny mb-1">Product</div>
                      <PickerWithAdd testid={`order-item-product-${i}`}
                        value={it.product}
                        onChange={(v, meta) => { setRow(i, "product", v); if (meta) applyProductPrefill(i, meta); }}
                        options={productOpts}
                        placeholder="Choose product"
                        addLabel="product"
                        onCreate={async (name) => { const o = await createProduct(name); setProducts(ps => [o.meta, ...ps]); return o; }}
                      /></div>
                    <div className="col-span-1 md:col-span-2"><div className="label-tiny mb-1">Color</div>
                      <PickerWithAdd testid={`order-item-color-${i}`}
                        value={it.color} onChange={v => setRow(i, "color", v)}
                        options={COMMON_COLORS.map(c => ({ value: c, label: c }))}
                        placeholder="Color" addLabel="color"
                        onCreate={async (name) => ({ value: name, label: name })}
                      /></div>
                    <div className="col-span-1 md:col-span-2"><div className="label-tiny mb-1">Customization</div>
                      <select className="input-field" value={it.customization} onChange={e => setRow(i, "customization", e.target.value)}>
                        {CUSTOMIZATION_TYPES.map(c => <option key={c} value={c}>{c || "—"}</option>)}
                      </select></div>
                    <div className="col-span-1 md:col-span-1"><div className="label-tiny mb-1">Qty</div>
                      <input type="number" className="input-field" value={it.qty || ""} onChange={e => setRow(i, "qty", Number(e.target.value)||0)} data-testid={`order-item-qty-${i}`}/></div>
                    <div className="col-span-1 md:col-span-2"><div className="label-tiny mb-1">Unit ₹</div>
                      <div className="flex gap-1">
                        <input type="number" className="input-field" value={it.unit_price || ""} onChange={e => setRow(i, "unit_price", Number(e.target.value)||0)} data-testid={`order-item-price-${i}`}/>
                        <button type="button" onClick={() => setOpenTee(openTee === i ? -1 : i)} className="btn-ghost text-xs px-2" title="Print locations" data-testid={`order-tee-open-${i}`}><Shirt size={13}/></button>
                      </div>
                    </div>
                    <div className="col-span-2 md:col-span-1 flex justify-end">
                      {form.items.length > 1 && <button type="button" onClick={() => rmRow(i)} className="p-2 text-[#B83A3A] hover:bg-[#F7DFDF] rounded"><Trash2 size={14}/></button>}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-10 gap-2 mt-2">
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">GST %</div>
                      <input type="number" step="1" className="input-field" value={it.gst_percent ?? 18}
                        onChange={e=>setRow(i,"gst_percent",Number(e.target.value)||0)} data-testid={`order-item-gst-${i}`}/>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">Cust ₹/pc</div>
                      <input type="number" className="input-field" value={it.customization_price ?? 0}
                        onChange={e=>setRow(i,"customization_price",Number(e.target.value)||0)} data-testid={`order-item-custprice-${i}`}/>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">3XL+ ₹/pc</div>
                      <input type="number" className="input-field" value={it.oversize_surcharge ?? 0}
                        onChange={e=>setRow(i,"oversize_surcharge",Number(e.target.value)||0)} data-testid={`order-item-oversize-${i}`}/>
                    </div>
                    <div className="md:col-span-2">
                      <div className="label-tiny mb-1">HSN</div>
                      <input className="input-field" value={it.hsn_code || ""} onChange={e=>setRow(i,"hsn_code",e.target.value)}/>
                    </div>
                    <div className="col-span-2 md:col-span-2">
                      <div className="label-tiny mb-1">Line total</div>
                      <div className="input-field bg-[#F6F1E7] mono">{inr((Number(it.qty)||0)*((Number(it.unit_price)||0)+(Number(it.customization_price)||0)) + (Number(it.oversize_surcharge)||0) * oversizeCount(it))}</div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="label-tiny mb-1.5">Size grid <span className="normal-case text-[10px]">(auto-sums into Qty)</span></div>
                    <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
                      {SIZES.map(sz => (
                        <div key={sz}>
                          <div className="text-[10px] text-center text-[#757169] mb-0.5 font-medium">{sz}</div>
                          <input type="number" min="0" className="input-field text-center px-1"
                            value={it.sizes?.[sz] ?? ""} placeholder="—"
                            data-testid={`order-item-size-${i}-${sz}`}
                            onChange={e => {
                              const v = e.target.value === "" ? "" : Number(e.target.value);
                              const sizes = { ...(it.sizes || {}) };
                              if (v === "" || v === 0) delete sizes[sz]; else sizes[sz] = v;
                              const total = Object.values(sizes).reduce((a, b) => a + Number(b || 0), 0);
                              setForm(f => ({ ...f, items: f.items.map((x, idx) => idx === i ? { ...x, sizes, qty: total || x.qty } : x) }));
                            }} />
                        </div>
                      ))}
                    </div>
                  </div>

                  {openTee === i && (
                    <div className="mt-3 p-3 bg-white border border-[#EAE5DB] rounded-md">
                      <div className="flex items-center justify-between mb-2">
                        <div className="label-tiny">Print / embroidery locations & pricing</div>
                        <button type="button" className="text-xs text-[#75706A] hover:text-[#211F1D]" onClick={() => setOpenTee(-1)}>Close</button>
                      </div>
                      <TShirtDiagram
                        value={it.print_locations || {}}
                        onChange={v => setRow(i, "print_locations", v)}
                        onCost={(cost) => {
                          setForm(f => ({
                            ...f,
                            items: f.items.map((x, idx) => idx === i
                              ? { ...x, customization_price: Math.round(cost * 100) / 100 }
                              : x)
                          }));
                        }}
                      />
                    </div>
                  )}
                  {Object.keys(it.print_locations || {}).length > 0 && openTee !== i && (
                    <div className="mt-2 text-xs text-[#75706A] flex flex-wrap gap-1.5 items-center">
                      <span className="label-tiny">Print at:</span>
                      {Object.keys(it.print_locations).map(k => <span key={k} className="badge badge-info">{k.replace(/_/g, " ")}</span>)}
                      <span className="ml-2 text-[10px]">· print cost ₹{totalPrintCost(it.print_locations).total.toFixed(0)}/pc</span>
                    </div>
                  )}
                </div>{/* /desktop */}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-start pt-4 border-t border-[#EAE5DB] flex-wrap gap-4">
            <div className="text-sm text-[#757169] space-y-0.5">
              <div>Subtotal: <span className="mono text-[#2C2B29]">{inr(totals.sub)}</span></div>
              {totals.breakup.map(b => (
                <div key={b.slab}>GST @ {b.slab}% on {inr(b.subtotal)}: <span className="mono text-[#2C2B29]">{inr(b.gst)}</span></div>
              ))}
              <div className="font-display text-lg font-bold text-[#2C2B29]">Total: {inr(totals.total)}</div>
              <div>{Number(form.advance_amount) > 0 ? "Balance due" : "Full balance due"}: <span className="mono">{inr(totals.total - Number(form.advance_amount || 0))}</span></div>
            </div>
            <button type="submit" className="btn-primary" data-testid="order-submit">Review & create order</button>
          </div>
        </form>
      )}

      <FinalReview
        open={showReview}
        onClose={() => setShowReview(false)}
        onConfirm={submit}
        title="Create order?"
        submittingLabel="Create order"
        submitting={submitting}
        customerName={form.customer_name}
        customerPhone={form.customer_phone}
        items={form.items}
        totals={totals}
        advanceAmount={form.advance_amount}
        extraRows={[
          form.expected_delivery_at ? { label: "Expected delivery", value: form.expected_delivery_at } : null,
          form.manual_order_id ? { label: "Manual order #", value: form.manual_order_id } : null,
          form.notes ? { label: "Notes", value: form.notes } : null,
        ].filter(Boolean)}
      />

      <div className="card-surface overflow-x-auto">
        <OrdersTable items={items} onRowClick={setPopup} onDeleted={load} />
      </div>

      <DetailPopup open={!!popup} onClose={() => setPopup(null)}
        subtitle="Order" title={popup?.order_no || ""}
        actions={popup && <>
          <Link to={`/orders/${popup.id}`} className="btn-primary text-xs">Open order →</Link>
          <a href={`/print/order/${popup.id}`} target="_blank" rel="noreferrer" className="btn-ghost text-xs">Print form</a>
        </>}>
        {popup && <>
          <div><span className="text-[#75706A]">Customer:</span> <b>{popup.customer_name}</b>{popup.customer_phone && <span className="text-xs text-[#75706A]"> · {popup.customer_phone}</span>}</div>
          <div><span className="text-[#75706A]">Stage:</span> <span className={`badge ${stageBadge[popup.stage] || "badge-neutral"}`}>{popup.stage}</span></div>
          <div><span className="text-[#75706A]">Items:</span> {popup.items?.length} · <span className="text-[#75706A]">Total:</span> <span className="mono">{inr(popup.total)}</span></div>
          {popup.advance_amount > 0 && <div><span className="text-[#75706A]">Advance:</span> <span className="mono">{inr(popup.advance_amount)}</span> · Balance <span className="mono">{inr(Number(popup.total) - Number(popup.advance_amount || 0))}</span></div>}
          {popup.expected_delivery_at && <div><span className="text-[#75706A]">Expected delivery:</span> {popup.expected_delivery_at.slice(0, 10)}</div>}
          <div className="mt-3 pt-3 border-t border-[#E7E1D3]">
            <div className="label-tiny mb-1">Line items</div>
            {popup.items?.map((it, i) => (
              <div key={i} className="text-xs py-1 flex justify-between">
                <span>{it.product}{it.color && ` · ${it.color}`}{it.customization && ` · ${it.customization}`}{it.print_locations && Object.keys(it.print_locations).length > 0 && ` · ${Object.keys(it.print_locations).join(", ")}`}</span>
                <span className="mono">{it.qty} × {inr(it.unit_price)}</span>
              </div>
            ))}
          </div>
        </>}
      </DetailPopup>
    </div>
  );
}
